import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * On-page alert/callout. Two tonalities per status:
 *
 *   - Tinted (info / success / warning / danger): light fill + colored text +
 *     colored icon. Use for inline messages that need to be noticed but not
 *     interrupt — form-level errors, page-level "heads up" notes, etc.
 *   - Filled (-solid): saturated background + white text + white icon. Use
 *     for genuinely urgent / direct callouts. Use sparingly — if everything's
 *     filled, nothing stands out.
 *
 * For TRANSIENT feedback (saved/synced/failed → fades on its own), reach for
 * the `<Toaster />` + `toast()` from sonner instead of <Alert>. Alerts persist
 * until the user dismisses or navigates.
 */

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground border-border [&>svg]:text-foreground",
        info: "bg-info-light text-info border-info/40 [&>svg]:text-info",
        success: "bg-success-light text-success border-success/40 [&>svg]:text-success",
        warning: "bg-warning-light text-warning border-warning/40 [&>svg]:text-warning",
        danger: "bg-danger-light text-danger border-danger/40 [&>svg]:text-danger",
        // Filled — saturated background, white text. For urgent/direct callouts.
        "info-solid": "bg-info text-info-foreground border-transparent [&>svg]:text-info-foreground",
        "success-solid":
          "bg-success text-success-foreground border-transparent [&>svg]:text-success-foreground",
        "warning-solid":
          "bg-warning text-warning-foreground border-transparent [&>svg]:text-warning-foreground",
        "danger-solid":
          "bg-danger text-danger-foreground border-transparent [&>svg]:text-danger-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5 ref={ref} className={cn("mb-1 font-semibold leading-none tracking-tight", className)} {...props} />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm opacity-90 [&_p]:leading-relaxed", className)} {...props} />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
